import React from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/subpage.css";
import "../../Styles/pages/how-ann-compares.css";
import useReveal from "./useReveal";

export default function HowAnnCompares() {
  const ref = useReveal();

  return (
    <div className="page ann-subpage" ref={ref}>
      {/* HERO */}
      <section className="sub-hero" id="top">
        <div className="frame">
          <Link to="/" className="back">← Ann · Hiring intelligence</Link>
          <span>How Ann compares</span>
        </div>
        <span className="eye">Where Ann fits</span>
        <h1>Already have hiring software? Here's where Ann <em>fits</em><span className="period" /></h1>
        <p className="lede-xl">
          Most teams already run an applicant tracking system, maybe a sourcing tool, maybe a general AI assistant. <b>Ann doesn't replace any of them</b> — it does the one thing none was built to do: <em>judge candidates on evidence, and explain the call</em>.
        </p>
        <div className="ctas">
          <a href="#matrix" className="btn btn-primary btn-lg">See the comparison</a>
          <Link to="/why-ann" className="btn btn-secondary btn-lg">Why a new category</Link>
        </div>
      </section>

      {/* VS ATS */}
      <section className="sub-section">
        <div className="sub-head" style={{ marginBottom: 40 }}>
          <span className="eye">vs. an applicant tracking system</span>
          <h2 className="sub-h2">"Isn't our <em>ATS</em> enough?"</h2>
        </div>
        <div className="contrast">
          <div className="them">
            <div className="tag">What an ATS does</div>
            <h3>Stores candidates. Tracks stages. Logs notes.</h3>
            <p>An applicant tracking system is a system of record. It tells you who applied and where they are in the process — but it was never built to tell you whether they're any good.</p>
          </div>
          <div className="us">
            <div className="tag">What Ann does</div>
            <h3>Ann <em>judges</em> — and writes it back.</h3>
            <p><b>Ann evaluates each candidate on evidence and explains why</b>, then syncs that judgement into your ATS. The record stays where it is; the intelligence is the part that was missing.</p>
          </div>
        </div>
      </section>

      {/* VS SOURCING */}
      <section className="sub-section bone">
        <div className="sub-head" style={{ marginBottom: 40 }}>
          <span className="eye">vs. a sourcing tool</span>
          <h2 className="sub-h2">"Don't sourcing tools already <em>find</em> people?"</h2>
        </div>
        <div className="contrast">
          <div className="them">
            <div className="tag">What sourcing tools do</div>
            <h3>Find more candidates. Add volume.</h3>
            <p>Sourcing tools widen the top of the funnel. But more résumés into a process that can't read them carefully just means more triage — and the best people stay buried, mis-described.</p>
          </div>
          <div className="us">
            <div className="tag">What Ann does</div>
            <h3>Finds the <em>right</em> ones — and says why.</h3>
            <p><b>Ann surfaces the candidates who fit, including the ~60% a keyword search misses</b>, and reads every one on the work they've actually done. Volume becomes a shortlist with a reason attached.</p>
          </div>
        </div>
      </section>

      {/* VS GENERAL AI */}
      <section className="sub-section">
        <div className="sub-head" style={{ marginBottom: 40 }}>
          <span className="eye">vs. a general AI assistant</span>
          <h2 className="sub-h2">"Can't we just use a <em>general AI</em>?"</h2>
        </div>
        <div className="contrast">
          <div className="them">
            <div className="tag">What a general AI gives you</div>
            <h3>A plausible paragraph. An opinion.</h3>
            <p>Ask a general-purpose assistant to screen a candidate and you'll get something readable — but unstructured, inconsistent between candidates, and impossible to audit when someone asks how you decided.</p>
          </div>
          <div className="us">
            <div className="tag">What Ann gives you</div>
            <h3>A structured, comparable <em>evaluation</em>.</h3>
            <p><b>Same format every time, evidence cited, comparable across the slate, defensible to a board.</b> Ann is built for one job — hiring — and it shows in everything the general tool can't give you.</p>
          </div>
        </div>
      </section>

      {/* MATRIX */}
      <section className="sub-section bone" id="matrix">
        <div className="sub-head">
          <span className="eye">At a glance</span>
          <h2 className="sub-h2">Four kinds of tool. <em>Four</em> different jobs.</h2>
          <p className="lede">Ann isn't a better version of what you have — it occupies a layer the others leave empty.</p>
        </div>
        <div className="matrix">
          <div className="mrow head">
            <div className="mc lbl" />
            <div className="mc">Applicant tracking</div>
            <div className="mc">Sourcing tool</div>
            <div className="mc">General AI</div>
            <div className="mc ann">Ann</div>
          </div>
          <div className="mrow">
            <div className="mc lbl">What it's for</div>
            <div className="mc">Recording the process</div>
            <div className="mc">Finding more candidates</div>
            <div className="mc">General-purpose answers</div>
            <div className="mc ann"><b>Judging candidates</b></div>
          </div>
          <div className="mrow">
            <div className="mc lbl">What it optimises</div>
            <div className="mc">Compliance & tracking</div>
            <div className="mc">Pipeline volume</div>
            <div className="mc">Fluent text</div>
            <div className="mc ann"><b>Decision quality</b></div>
          </div>
          <div className="mrow">
            <div className="mc lbl">The output</div>
            <div className="mc">A status</div>
            <div className="mc">More résumés</div>
            <div className="mc">An opinion</div>
            <div className="mc ann"><b>An evidence-backed verdict</b></div>
          </div>
          <div className="mrow">
            <div className="mc lbl">Auditable?</div>
            <div className="mc">Process only</div>
            <div className="mc">No</div>
            <div className="mc">No</div>
            <div className="mc ann"><b>Yes — every judgement</b></div>
          </div>
          <div className="mrow">
            <div className="mc lbl">Where it sits</div>
            <div className="mc">System of record</div>
            <div className="mc">Top of funnel</div>
            <div className="mc">Alongside everything</div>
            <div className="mc ann"><b>Between candidates & the decision</b></div>
          </div>
        </div>
      </section>

      {/* FAQ (mirrors JSON-LD) */}
      <section className="sub-section">
        <div className="sub-head">
          <span className="eye">Common questions</span>
          <h2 className="sub-h2">The questions buyers <em>search</em> for.</h2>
        </div>
        <div className="cmp-faq">
          <details className="faq-item" open>
            <summary><span className="num">01</span><span className="q">Is an applicant tracking system enough for hiring?</span><span className="ico">+</span></summary>
            <p className="a">An ATS stores candidates and tracks what stage they're at — it doesn't judge whether a candidate is right for the role. Ann evaluates candidates on evidence, explains every judgement, and writes it back into your ATS. Different job, different layer; most teams run both.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">02</span><span className="q">Do I still need Ann if I have a sourcing tool?</span><span className="ico">+</span></summary>
            <p className="a">Sourcing tools find more candidates and add volume. Ann finds the <em>right</em> candidates — including the ~60% keyword search misses — and tells you why each fits, with cited evidence. Sourcing adds volume; Ann adds judgement.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">03</span><span className="q">Can a general AI assistant screen candidates?</span><span className="ico">+</span></summary>
            <p className="a">A general assistant can give a plausible paragraph, but the output is unstructured, inconsistent between candidates, and impossible to audit. Ann produces a structured, comparable, evidence-backed evaluation — same format every time, sources cited, a trail you can defend.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">04</span><span className="q">What is hiring intelligence?</span><span className="ico">+</span></summary>
            <p className="a">Software that understands a role the way an experienced recruiter would, then finds, reads, and evaluates candidates on evidence of what they can actually do — and explains every judgement. It's the layer between the candidates and the decision, distinct from an ATS, a sourcing tool, or a general AI assistant.</p>
          </details>
          <details className="faq-item">
            <summary><span className="num">05</span><span className="q">Does Ann replace my existing hiring software?</span><span className="ico">+</span></summary>
            <p className="a">No. Ann sits in a layer your other tools don't occupy. It works alongside your ATS and sourcing tools — syncing candidates, stages, and notes both ways — and adds the evaluation those tools were never built to provide.</p>
          </details>
        </div>
      </section>

      {/* CTA */}
      <section className="sub-cta">
        <span className="eye">See the layer you're missing</span>
        <h2>Run Ann <em>next to</em> what you have.</h2>
        <p className="sub">Fifteen minutes on a real role. See what evidence-backed judgement adds to the tools you already run.</p>
        <div className="ctas">
          <Link to="/contact-us" className="btn btn-primary btn-lg">Book a demo</Link>
          <Link to="/why-ann" className="btn btn-secondary btn-lg">Why Ann</Link>
        </div>
      </section>
    </div>
  );
}
